import React, { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    LogOut,
    LayoutDashboard,
    UserPlus,
    Megaphone,
    Receipt,
    MessageSquare,
    Users,
    ShieldCheck,
    Repeat,
    Edit3,
    Sparkles,
    Bell,
    ChevronDown,
    MoreHorizontal,
    Check,
    X,
    Shield,
    Home,
    Car,
    Bike,
} from "lucide-react";
import EditProfileModal from "@/components/EditProfileModal";
import NotificationModal from "@/components/NotificationModal";
import NotificationPromptBanner from "@/components/NotificationPromptBanner";
import { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import { useGeofence } from "@/contexts/GeofenceContext";
import GuardGeofenceBlock from "@/components/GuardGeofenceBlock";
import ResidentAwayBanner from "@/components/ResidentAwayBanner";
import LocationStatusPill from "@/components/LocationStatusPill";
import {
    isPushSupported,
    getNotificationPermission,
    isCurrentDeviceSubscribed,
    subscribeUserToPush,
    syncSubscriptionWithBackend,
} from "@/lib/pushNotifications";

function formatTimeSafe(dateStr) {
    if (!dateStr) return "";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

const navByRole = {
    admin: [
        { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
        { to: "/visitors", label: "Visitors", icon: UserPlus },
        { to: "/notices", label: "Notices", icon: Megaphone },
        { to: "/bills", label: "Bills", icon: Receipt },
        { to: "/daily-help", label: "Daily Help", icon: Sparkles },
        { to: "/complaints", label: "Complaints", icon: MessageSquare },
        { to: "/directory", label: "Directory", icon: Users },
    ],
    resident: [
        { to: "/dashboard", label: "Home", icon: LayoutDashboard },
        { to: "/visitors", label: "My Visitors", icon: UserPlus },
        { to: "/notices", label: "Notices", icon: Megaphone },
        { to: "/bills", label: "Bills", icon: Receipt },
        { to: "/complaints", label: "Complaints", icon: MessageSquare },
        { to: "/directory", label: "Directory", icon: Users },
    ],
    guard: [
        { to: "/guard", label: "Gate", icon: ShieldCheck },
        { to: "/visitors", label: "Logs", icon: UserPlus },
    ],
};

const roleLabels = {
    admin: "Admin",
    resident: "Resident",
    guard: "Guard",
};

export default function Layout() {
    const { user, setUser, logout, refresh } = useAuth();
    const { isInside, status } = useGeofence();
    const navigate = useNavigate();
    const location = useLocation();
    const items = navByRole[user?.role] || [];
    const [switching, setSwitching] = useState(false);
    const [switchingRole, setSwitchingRole] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [roleMenuOpen, setRoleMenuOpen] = useState(false);
    const [moreNavOpen, setMoreNavOpen] = useState(false);
    const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
    const [pendingWalkins, setPendingWalkins] = useState([]);
    const [decidingId, setDecidingId] = useState(null);

    const roleMenuRef = useRef(null);
    const moreNavRef = useRef(null);

    const canSwitch = Boolean(
        user?.role === "admin" ||
        user?.is_owner ||
        user?.is_test_admin ||
        user?.can_switch_roles ||
        user?.email?.toLowerCase() === "aws.tilak007@gmail.com"
    );

    // Split items for laptop screens where 7 items crowd out the bar:
    // Show top 4 primary items, collapse remainder into "More" on < xl screens
    const primaryItems = items.slice(0, 4);
    const overflowItems = items.slice(4);

    // Close dropdowns on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (roleMenuRef.current && !roleMenuRef.current.contains(e.target)) {
                setRoleMenuOpen(false);
            }
            if (moreNavRef.current && !moreNavRef.current.contains(e.target)) {
                setMoreNavOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close menus on route change
    useEffect(() => {
        setMoreNavOpen(false);
        setMobileMoreOpen(false);
        setRoleMenuOpen(false);
    }, [location.pathname]);

    // Automatically ensure active push subscription is synced if permission was granted
    useEffect(() => {
        if (!isPushSupported() || !user) return;
        if (getNotificationPermission() === "granted") {
            syncSubscriptionWithBackend().catch(() => {});
        }
    }, [user?.user_id]);

    const loadPendingWalkins = async () => {
        if (!user || (!user.flat_no && user.role !== "resident")) {
            setPendingWalkins([]);
            return;
        }
        try {
            const res = await api.get("/walkins/pending/me");
            setPendingWalkins(res.data || []);
        } catch (err) {}
    };

    useEffect(() => {
        loadPendingWalkins();
        const iv = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                loadPendingWalkins();
            }
        }, 6000);
        return () => clearInterval(iv);
    }, [user?.user_id, user?.role]);

    const handleWalkinDecision = async (w, decision) => {
        setDecidingId(w.walkin_id);
        try {
            await api.post(`/walkins/${w.walkin_id}/decision`, { decision });
            setPendingWalkins((prev) => prev.filter((it) => it.walkin_id !== w.walkin_id));
            toast.success(decision === "approved" ? `✓ Approved entry for ${w.name}` : `✕ Denied entry for ${w.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        } finally {
            setDecidingId(null);
        }
    };

    const switchTo = async (role) => {
        if (switching || user?.role === role) return;
        setSwitching(true);
        setSwitchingRole(role);
        setRoleMenuOpen(false);
        try {
            const res = await api.post("/auth/switch-role", { role });
            if (res.data) {
                setUser(res.data);
            } else {
                await refresh();
            }
            toast.success(`Switched to ${roleLabels[role] || role}`);
            navigate(role === "guard" ? "/guard" : "/dashboard");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to switch role");
        } finally {
            setSwitching(false);
            setSwitchingRole(null);
        }
    };

    const activeOverflowItem = overflowItems.find((it) => location.pathname === it.to);
    const isOverflowActive = !!activeOverflowItem;

    return (
        <div className="min-h-screen bg-nest-base flex flex-col selection:bg-nest-clay selection:text-white">
            {/* Header */}
            <header
                className="fixed top-0 left-0 right-0 z-40 bg-white/98 md:bg-white/95 md:backdrop-blur-md border-b border-nest-line transform-gpu will-change-transform"
                style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 0.25rem)" }}
                data-testid="app-header"
            >
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-4">
                    {/* Brand Emblem & Name - Never shrinks, guaranteed visible */}
                    <button
                        onClick={() => navigate(user?.role === "guard" ? "/guard" : "/dashboard")}
                        className="flex items-center gap-2.5 shrink-0 select-none group text-left cursor-pointer transition-transform active:scale-95"
                        data-testid="brand-home-btn"
                        title="Go to Home"
                    >
                        <div className="h-9 w-9 rounded-xl bg-nest-ink text-white grid place-items-center font-display font-bold text-base shadow-xs group-hover:bg-nest-clay transition-colors shrink-0">
                            N
                        </div>
                        <div className="leading-tight flex flex-col justify-center">
                            <span className="font-display text-base sm:text-lg font-bold tracking-tight text-nest-ink group-hover:text-nest-clay transition-colors whitespace-nowrap">
                                NeighborNest
                            </span>
                            <div className="flex items-center gap-1.5">
                                <span className="inline-flex items-center px-1.5 py-0.2 rounded-md bg-nest-alt/80 text-[9px] font-bold uppercase tracking-wider text-nest-ink2 border border-nest-line">
                                    {user?.role}
                                </span>
                                {user?.tower && user?.flat_no && (
                                    <span className="text-[10px] text-nest-ink3 hidden sm:inline font-mono">
                                        {user.tower}-{user.flat_no}
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>

                    {/* Desktop & Laptop Navigation - Never overflows or mixes with utility items */}
                    <nav className="hidden md:flex items-center gap-1 min-w-0" aria-label="Main Navigation">
                        {primaryItems.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `px-2.5 lg:px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                                        isActive
                                            ? "bg-nest-ink text-white shadow-xs"
                                            : "text-nest-ink2 hover:text-nest-ink hover:bg-nest-alt/70"
                                    }`
                                }
                            >
                                <it.icon size={13} className="shrink-0" />
                                <span>{it.label}</span>
                            </NavLink>
                        ))}

                        {overflowItems.length > 0 && (
                            <div className="relative shrink-0" ref={moreNavRef}>
                                <button
                                    type="button"
                                    onClick={() => setMoreNavOpen(!moreNavOpen)}
                                    data-testid="nav-more-dropdown-btn"
                                    className={`px-2.5 lg:px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 flex items-center gap-1.5 whitespace-nowrap cursor-pointer shrink-0 ${
                                        isOverflowActive
                                            ? "bg-nest-ink text-white shadow-xs"
                                            : moreNavOpen
                                            ? "bg-nest-alt text-nest-ink border border-nest-line"
                                            : "text-nest-ink2 hover:text-nest-ink hover:bg-nest-alt/70"
                                    }`}
                                    title="More Services"
                                >
                                    {activeOverflowItem ? (
                                        <span className="flex items-center gap-1.5">
                                            <activeOverflowItem.icon size={13} className="shrink-0" />
                                            <span>{activeOverflowItem.label}</span>
                                        </span>
                                    ) : (
                                        <span>More</span>
                                    )}
                                    <ChevronDown size={12} className={`transition-transform duration-200 shrink-0 ${moreNavOpen ? "rotate-180" : ""}`} />
                                </button>

                                {moreNavOpen && (
                                    <div className="absolute left-0 mt-2 w-48 bg-white border border-nest-line rounded-xl shadow-xl py-1.5 z-50 animate-fade-in">
                                        <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-nest-ink3 border-b border-nest-line mb-1">
                                            More Services
                                        </p>
                                        {overflowItems.map((it) => (
                                            <NavLink
                                                key={it.to}
                                                to={it.to}
                                                onClick={() => setMoreNavOpen(false)}
                                                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                className={({ isActive }) =>
                                                    `px-3 py-2 text-xs font-medium flex items-center gap-2 transition-colors ${
                                                        isActive
                                                            ? "bg-nest-ink/10 text-nest-clay font-bold"
                                                            : "text-nest-ink2 hover:text-nest-ink hover:bg-nest-alt/70"
                                                    }`
                                                }
                                            >
                                                <it.icon size={14} className="shrink-0" />
                                                <span>{it.label}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>

                    {/* Right Utility Bar: Location Pill, Role Switcher, Profile, Bell, Logout */}
                    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                        <LocationStatusPill />
                        {canSwitch && (
                            <div className="relative" ref={roleMenuRef}>
                                {/* Wide Desktop (>= 1280px): Sleek Segmented Switcher */}
                                <div
                                    className="hidden xl:flex items-center gap-0.5 border border-nest-line rounded-xl p-1 bg-nest-alt/40"
                                    data-testid="role-switcher"
                                >
                                    <Repeat size={12} className="ml-1.5 mr-1 text-nest-ink3" />
                                    {["admin", "resident", "guard"].map((r) => (
                                        <button
                                            key={r}
                                            disabled={switching || user?.role === r}
                                            onClick={() => switchTo(r)}
                                            data-testid={`switch-to-${r}`}
                                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg transition-all duration-150 cursor-pointer ${
                                                user?.role === r
                                                    ? "bg-nest-ink text-white shadow-xs"
                                                    : "text-nest-ink2 hover:bg-nest-alt hover:text-nest-ink"
                                            }`}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>

                                {/* Laptop & Tablet (640px to 1279px): Compact Role Dropdown Pill */}
                                <button
                                    type="button"
                                    onClick={() => setRoleMenuOpen(!roleMenuOpen)}
                                    className="hidden sm:flex xl:hidden items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-nest-line bg-nest-alt/50 hover:bg-nest-alt text-nest-ink text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
                                    title="Switch Role"
                                >
                                    <Repeat size={12} className="text-nest-ink3" />
                                    <span>{user?.role}</span>
                                    <ChevronDown size={12} className={`text-nest-ink3 transition-transform ${roleMenuOpen ? "rotate-180" : ""}`} />
                                </button>

                                {/* Mobile (< 640px): 1-tap mini role pill */}
                                <div
                                    className="flex sm:hidden items-center border border-nest-line rounded-lg p-0.5 bg-nest-alt/60 shrink-0"
                                    data-testid="mobile-role-switcher"
                                    title="Switch role"
                                >
                                    {["admin", "resident", "guard"].map((r) => {
                                        const isCurrent = user?.role === r;
                                        const isSwitchingThis = switchingRole === r;
                                        const label = r === "admin" ? "Adm" : r === "resident" ? "Res" : "Grd";
                                        return (
                                            <button
                                                key={r}
                                                disabled={switching || isCurrent}
                                                onClick={() => switchTo(r)}
                                                className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded transition ${
                                                    isCurrent
                                                        ? "bg-nest-ink text-white shadow-xs"
                                                        : isSwitchingThis
                                                        ? "bg-nest-clay text-white animate-pulse"
                                                        : "text-nest-ink3 hover:text-nest-ink"
                                                }`}
                                            >
                                                {isSwitchingThis ? "…" : label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Floating Dropdown for Laptop */}
                                {roleMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-44 bg-white border border-nest-line rounded-xl shadow-xl py-1.5 z-50 animate-fade-in">
                                        <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-nest-ink3 border-b border-nest-line mb-1">
                                            Switch Active Mode
                                        </p>
                                        {[
                                            { r: "admin", label: "Admin Console", icon: Shield },
                                            { r: "resident", label: "Resident Portal", icon: Home },
                                            { r: "guard", label: "Gate Console", icon: ShieldCheck },
                                        ].map(({ r, label, icon: Icon }) => (
                                            <button
                                                key={r}
                                                disabled={switching || user?.role === r}
                                                onClick={() => switchTo(r)}
                                                className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                    user?.role === r
                                                        ? "bg-nest-ink/5 text-nest-clay font-bold"
                                                        : "text-nest-ink2 hover:text-nest-ink hover:bg-nest-alt/60"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Icon size={14} className={user?.role === r ? "text-nest-clay" : "text-nest-ink3"} />
                                                    <span>{label}</span>
                                                </div>
                                                {user?.role === r && <Check size={14} className="text-nest-clay" />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Profile Button - Compact on laptop, expandable */}
                        <button
                            onClick={() => setProfileOpen(true)}
                            data-testid="edit-profile-btn"
                            className="flex items-center gap-2 p-1 sm:px-2.5 sm:py-1 rounded-xl border border-nest-line hover:border-nest-clay hover:bg-nest-alt/70 transition-all shrink-0 cursor-pointer group"
                            title="Edit Profile"
                        >
                            {user?.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-full object-cover ring-1 ring-nest-line group-hover:ring-nest-clay transition shrink-0"
                                />
                            ) : (
                                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-nest-ink text-white text-xs font-semibold grid place-items-center shrink-0">
                                    {user?.name?.slice(0, 1) || "U"}
                                </div>
                            )}
                            <div className="hidden xl:block leading-tight text-left max-w-[110px] xl:max-w-[140px]">
                                <p className="text-xs font-semibold text-nest-ink group-hover:text-nest-clay truncate flex items-center gap-1 transition-colors">
                                    <span className="truncate">{user?.name?.split(" ")[0]}</span>
                                    <Edit3 size={10} className="text-nest-ink3 opacity-70 shrink-0" />
                                </p>
                                <p className="text-[10px] text-nest-ink3 truncate font-mono">
                                    {user?.tower && user?.flat_no ? `${user.tower}-${user.flat_no}` : user?.email}
                                </p>
                            </div>
                        </button>

                        {/* Notification Bell Button */}
                        <button
                            onClick={() => setNotifOpen(true)}
                            data-testid="notifications-bell-btn"
                            className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl border border-nest-line hover:border-nest-clay hover:bg-nest-alt/70 grid place-items-center text-nest-ink2 hover:text-nest-ink transition-colors relative shrink-0 cursor-pointer"
                            title={pendingWalkins.length > 0 ? `${pendingWalkins.length} visitor(s) awaiting your approval` : "Push Notifications"}
                        >
                            <Bell size={15} />
                            {pendingWalkins.length > 0 ? (
                                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-600 text-[10px] font-black text-white flex items-center justify-center animate-pulse shadow-sm ring-2 ring-white">
                                    {pendingWalkins.length}
                                </span>
                            ) : (
                                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-nest-clay ring-2 ring-white"></span>
                            )}
                        </button>

                        {/* Logout Button */}
                        <button
                            onClick={logout}
                            data-testid="logout-btn"
                            className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl border border-nest-line hover:border-nest-err hover:text-nest-err hover:bg-red-50/50 grid place-items-center text-nest-ink2 transition-colors shrink-0 cursor-pointer"
                            title="Log out"
                        >
                            <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Notification Prompt Banner */}
            <NotificationPromptBanner />

            {/* Real-time In-App Gate Approval Alert Banner for Residents */}
            {pendingWalkins.length > 0 && (
                <div className="max-w-7xl mx-auto w-full px-3.5 sm:px-6 lg:px-8 pt-header-safe mb-2 animate-scale-up" data-testid="global-pending-walkin-alert">
                    <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/95 p-3.5 sm:p-5 shadow-lg shadow-amber-500/10 backdrop-blur-xs">
                        <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-ping inline-block" />
                                <h4 className="text-xs sm:text-sm font-black uppercase tracking-wider text-amber-950 flex items-center gap-1.5">
                                    <span>Gate Arrival Alert</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-bold">
                                        {pendingWalkins.length} Awaiting Approval
                                    </span>
                                </h4>
                            </div>
                            <span className="text-xs text-amber-800 font-bold">Flat {user?.tower && user?.flat_no ? `${user.tower}-${user.flat_no}` : user?.flat_no}</span>
                        </div>

                        <div className="grid gap-2.5">
                            {pendingWalkins.map((w) => (
                                <div
                                    key={w.walkin_id}
                                    className="bg-white rounded-xl border border-amber-300 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <img
                                            src={getVisitorPhotoSrc(w)}
                                            alt=""
                                            className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-cover border border-nest-line bg-nest-alt shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="font-bold text-base text-nest-ink truncate">{w.name}</p>
                                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">
                                                    {w.purpose || "Visitor"}
                                                </span>
                                                {w.vehicle_no && (
                                                    <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-nest-alt px-1.5 py-0.5 rounded border border-nest-line text-nest-ink">
                                                        {w.vehicle_type === "bike" ? <Bike size={11} className="text-nest-clay" /> : <Car size={11} className="text-nest-clay" />}
                                                        <span>{w.vehicle_no}</span>
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-nest-ink3 mt-0.5">
                                                At gate · Logged by guard {w.guard_name || "on duty"}{w.created_at ? ` · ${formatTimeSafe(w.created_at)}` : ""}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                                        <button
                                            type="button"
                                            disabled={decidingId === w.walkin_id}
                                            onClick={() => handleWalkinDecision(w, "rejected")}
                                            className="px-4 py-2 rounded-xl border-2 border-red-300 hover:bg-red-50 text-red-700 font-bold text-xs sm:text-sm transition cursor-pointer flex items-center gap-1.5 active:scale-95"
                                        >
                                            <X size={16} />
                                            <span>Deny</span>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={decidingId === w.walkin_id}
                                            onClick={() => handleWalkinDecision(w, "approved")}
                                            className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm transition cursor-pointer shadow-md shadow-emerald-600/20 flex items-center gap-1.5 active:scale-95"
                                        >
                                            <Check size={16} />
                                            <span>Approve Entry</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Resident Away Alert Banner */}
            {user?.role === "resident" && <ResidentAwayBanner />}

            {/* Main content with responsive padding and mobile bottom nav clearance */}
            <main className="max-w-7xl mx-auto w-full px-3.5 sm:px-6 lg:px-8 pt-header-safe pb-24 md:pb-12 flex-1">
                {user?.role === "guard" && !isInside ? (
                    <GuardGeofenceBlock />
                ) : (
                    <Outlet />
                )}
            </main>

            {/* Mobile Bottom Navigation Bar (< 768px) */}
            <nav
                className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-nest-line shadow-[0_-2px_12px_rgba(0,0,0,0.04)] pb-safe transform-gpu will-change-transform"
                data-testid="mobile-bottom-nav"
            >
                <div className="grid grid-flow-col auto-cols-fr items-center py-1.5 px-2">
                    {/* Guard: 3 tabs fit directly */}
                    {user?.role === "guard" ? (
                        items.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex flex-col items-center justify-center py-1 rounded-xl transition-all duration-150 ${
                                        isActive
                                            ? "text-nest-clay font-bold"
                                            : "text-nest-ink3 hover:text-nest-ink"
                                    }`
                                }
                            >
                                <it.icon size={19} className="shrink-0" />
                                <span className="text-[10px] tracking-tight mt-0.5">{it.label}</span>
                            </NavLink>
                        ))
                    ) : (
                        /* Resident & Admin: 4 primary tabs + "More" bottom sheet toggle */
                        <>
                            {primaryItems.map((it) => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center py-1 rounded-xl transition-all duration-150 ${
                                            isActive
                                                ? "text-nest-clay font-bold"
                                                : "text-nest-ink3 hover:text-nest-ink"
                                        }`
                                    }
                                >
                                    <it.icon size={19} className="shrink-0" />
                                    <span className="text-[10px] tracking-tight mt-0.5 truncate max-w-[64px]">{it.label}</span>
                                </NavLink>
                            ))}

                            {overflowItems.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setMobileMoreOpen(true)}
                                    className={`flex flex-col items-center justify-center py-1 rounded-xl transition-all duration-150 cursor-pointer ${
                                        isOverflowActive
                                            ? "text-nest-clay font-bold"
                                            : "text-nest-ink3 hover:text-nest-ink"
                                    }`}
                                    title="More Options"
                                >
                                    <MoreHorizontal size={19} className="shrink-0" />
                                    <span className="text-[10px] tracking-tight mt-0.5">More</span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </nav>

            {/* Mobile "More" Slide-up Sheet */}
            {mobileMoreOpen && (
                <div
                    className="md:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex flex-col justify-end animate-fade-in"
                    onClick={() => setMobileMoreOpen(false)}
                >
                    <div
                        className="bg-white rounded-t-3xl border-t border-nest-line p-5 pb-safe shadow-2xl animate-fade-up"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between pb-3 border-b border-nest-line">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-nest-clay"></div>
                                <h3 className="font-display text-base font-bold text-nest-ink">All Society Services</h3>
                            </div>
                            <button
                                onClick={() => setMobileMoreOpen(false)}
                                className="p-1 rounded-full text-nest-ink3 hover:text-nest-ink hover:bg-nest-alt transition cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-3 pt-4">
                            {items.map((it) => (
                                <NavLink
                                    key={it.to}
                                    to={it.to}
                                    onClick={() => setMobileMoreOpen(false)}
                                    data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center p-3 rounded-2xl border text-center transition-all ${
                                            isActive
                                                ? "bg-nest-ink text-white border-nest-ink shadow-sm"
                                                : "bg-nest-alt/40 border-nest-line text-nest-ink hover:bg-nest-alt"
                                        }`
                                    }
                                >
                                    <it.icon size={22} className="mb-1.5" />
                                    <span className="text-xs font-semibold">{it.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <EditProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
            <NotificationModal isOpen={notifOpen} onClose={() => setNotifOpen(false)} />
        </div>
    );
}
